/**
 * Width estimation for chart axis labels.
 *
 * Reserving space for a label column has to happen *before* G2 renders anything, so the real
 * rendered width is not available. Multiplying the character count by a single average glyph width
 * is only accurate for the character mix it was calibrated on: Open Sans advances range from 0.26em
 * (space) to 1.03em (`W`), so a flat average under-reserves ALL-CAPS text (product codes, SKU names)
 * by roughly a quarter and clips it.
 *
 * This sums per-character advances instead. It is pure and font-metric driven, so it behaves
 * identically in `happy-dom` as in a browser — unlike `canvas.measureText`, which is not implemented
 * in the test environment.
 *
 * This is a *reservation* estimate only. Preventing adjacent tick labels from colliding is G2's job,
 * via the transforms in `chartAxisLabelTransforms.ts`, which measure the labels G2 actually rendered.
 */

/**
 * Per-character advance widths for 'Open Sans' Regular, in em (advance ÷ font size).
 *
 * Only the classes whose spread actually matters are enumerated: upper- and lowercase Latin, plus
 * the separators common in product names. Digits, and anything unlisted, fall back to the constants
 * below. Values are rounded to two decimals; the residual error is well under a pixel per glyph.
 */
const GLYPH_WIDTH_EM: Readonly<Record<string, number>> = {
    A: 0.68, B: 0.69, C: 0.7, D: 0.77, E: 0.63, F: 0.58, G: 0.78, H: 0.75, I: 0.3,
    J: 0.3, K: 0.68, L: 0.58, M: 1.0, N: 0.8, O: 0.83, P: 0.66, Q: 0.83, R: 0.7,
    S: 0.62, T: 0.62, U: 0.78, V: 0.68, W: 1.03, X: 0.67, Y: 0.61, Z: 0.62,
    a: 0.61, b: 0.66, c: 0.54, d: 0.66, e: 0.62, f: 0.38, g: 0.61, h: 0.66, i: 0.28,
    j: 0.28, k: 0.59, l: 0.28, m: 1.0, n: 0.66, o: 0.65, p: 0.66, q: 0.66, r: 0.43,
    s: 0.51, t: 0.41, u: 0.66, v: 0.57, w: 0.85, x: 0.56, y: 0.57, z: 0.5,
    ' ': 0.26, '-': 0.35, '_': 0.5, '/': 0.42, '&': 0.75, '.': 0.32, ',': 0.32,
    ':': 0.32, ';': 0.32, '(': 0.38, ')': 0.38, '%': 0.93, '+': 0.67, "'": 0.23,
};

/** Open Sans renders digits on a single tabular advance, so all ten share one width. */
const DIGIT_WIDTH_EM = 0.64;

/** Advance assumed for an unlisted Latin-range character (symbols, accented letters). */
const FALLBACK_WIDTH_EM = 0.6;

/**
 * Advance assumed for CJK and other full-width scripts, which occupy a full em regardless of glyph.
 * The threshold is the start of the CJK Radicals Supplement block.
 */
const WIDE_SCRIPT_WIDTH_EM = 1.0;
const WIDE_SCRIPT_MIN_CODE_POINT = 0x2e80;

/** Font size in px that chart axis tick labels render at. */
export const AXIS_LABEL_FONT_SIZE_PX = 13;

/** Advance of a single character in em, at the font size the caller renders with. */
function glyphWidthEm(char: string): number {
    const known = GLYPH_WIDTH_EM[char];
    if (known !== undefined) return known;
    if (char >= '0' && char <= '9') return DIGIT_WIDTH_EM;
    if ((char.codePointAt(0) ?? 0) >= WIDE_SCRIPT_MIN_CODE_POINT) return WIDE_SCRIPT_WIDTH_EM;
    return FALLBACK_WIDTH_EM;
}

/**
 * Estimate the rendered width in px of `text` at `fontSizePx`, by summing per-character advances.
 *
 * @param text Text to measure. Non-string input is coerced; nullish measures as 0.
 * @param fontSizePx Font size the text renders at.
 */
export function estimateTextWidthPx(text: string, fontSizePx: number = AXIS_LABEL_FONT_SIZE_PX): number {
    if (!text) return 0;

    let widthEm = 0;
    for (const char of text) {
        widthEm += glyphWidthEm(char);
    }

    return widthEm * fontSizePx;
}
